import { useSelector } from 'react-redux'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { Link } from 'react-router-dom'
import { Grid, Hide, LoadingSpinner, Show } from '@homebotapp/hb-react-component-catalog'
import { SavedSearchModel } from '../../../types/savedSearches'
import { SavedSearchCriteriaCard } from './SavedSearchCriteriaCard'
import { PATH_HELPERS } from '../../../constants/navigation'
import { ListingCardNew } from '../ListingCardNew/ListingCardNew'
import { HOME_SEARCH_SAVED_SEARCHES_PAGE } from '../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { NoListingsEmptyState } from './NoListingsEmptyState'
import { SeeAllListingsButton } from './SeeAllListingsButton'
import { useGetListingsForArea } from '../../../hooks/listings/useGetListingsForArea'
import { ListingsOrderBy, ListingsOrderDirection, SearchArea } from '../../../api/gqlaxy/gql/generated/graphql'
import { useMemo } from 'react'

export interface SavedSearchProps {
  savedSearch: SavedSearchModel
}

export const SavedSearch = ({ savedSearch }: SavedSearchProps) => {
  const customerId = useSelector(selectCustomerId)
  const memoizedGetListingsArgs = useMemo(
    () => ({
      page: 0,
      perPage: 3,
      sortOrder: {
        orderBy: savedSearch?.sortOrder.orderBy || ListingsOrderBy.DaysOnMarket,
        orderDir: savedSearch?.sortOrder.orderDir || ListingsOrderDirection.Asc
      },
      filters: {
        hasSellerConcession: savedSearch?.filters?.hasSellerConcession || false,
        isAssumable: savedSearch?.filters?.isAssumable || false,
        maxAcres: savedSearch?.filters?.maxAcres || null,
        maxPrice: savedSearch?.filters?.maxPrice || null,
        maxSqft: savedSearch?.filters?.maxSqft || null,
        maxYearBuilt: savedSearch?.filters?.maxYearBuilt || null,
        minAcres: savedSearch?.filters?.minAcres || null,
        minBathroomsCount: savedSearch?.filters?.minBathroomsCount || null,
        minBedroomsCount: savedSearch?.filters?.minBedroomsCount || null,
        minPrice: savedSearch?.filters?.minPrice || null,
        minSqft: savedSearch?.filters?.minSqft || null,
        minYearBuilt: savedSearch?.filters?.minYearBuilt || null,
        propertyTypes: savedSearch?.filters?.propertyTypes || [],
        statuses: savedSearch?.filters.statuses || ['Active', 'Coming Soon'],
        hasOpenHouses: savedSearch?.filters.hasOpenHouses || false
      },
      searchArea: savedSearch?.searchArea as SearchArea
    }),
    [savedSearch]
  )
  const { isLoading, data: listingsData } = useGetListingsForArea(memoizedGetListingsArgs, { enabled: !!savedSearch })

  if (isLoading) {
    return <LoadingSpinner />
  }

  // handles when there are no listings to preview, NOT when there are no saved searches (that is handled highger up)
  // this encompasses the entire saved search row including the card with the search name, criteria, listings, and "see all" + delete buttons
  if (!listingsData || listingsData.length === 0) {
    return <NoListingsEmptyState savedSearch={savedSearch} />
  }

  return (
    <Grid
      padding={3}
      borderRadius={8}
      backgroundColor='card-bg-layer-1'
      gap={5}
      margin={5}
      templateColumns={{ base: 'minmax(200px, 1fr)', lg: 'repeat(auto-fit, minmax(200px, 1fr))' }}
    >
      {/* get search location display text from listingsData */}
      <SavedSearchCriteriaCard savedSearch={savedSearch} listingsData={listingsData} />

      {listingsData[0] && <ListingPreviewLinkCard customerId={customerId} listing={listingsData[0]} />}
      <Show above='lg'>
        {listingsData[1] && <ListingPreviewLinkCard customerId={customerId} listing={listingsData[1]} />}
        {listingsData[2] && <ListingPreviewLinkCard customerId={customerId} listing={listingsData[2]} />}
      </Show>
      {/* on small screen, "see all" button goes below the listings */}
      <Hide above='lg'>
        <SeeAllListingsButton customerId={customerId} savedSearch={savedSearch} listingsData={listingsData} />
      </Hide>
    </Grid >
  )
}

const ListingPreviewLinkCard = ({ customerId, listing }) => {
  return (
    <Link
      to={PATH_HELPERS.listing.buildPath(customerId, listing.id)}
      data-qa={`listing-link-${listing.id}`}
      key={listing.id}
    >
      <ListingCardNew
        showFavoriteBtn={false}
        listing={listing}
        tracking={{
          guid: 'hsxMXkXNB83NyYaTTNgy1J',
          ui_context: HOME_SEARCH_SAVED_SEARCHES_PAGE,
          descriptor: 'Listing Preview Clicked'
        }}
      />
    </Link>
  )
}
